// components/Attachments.js
import React, { useEffect, useState } from 'react';
import imageCompression from 'browser-image-compression';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';

const Attachments = ({ onNext, onPrevious }) => {
  const {
    setValue,
    getValues,
    register,
    formState: { errors },
    trigger
  } = useFormContext();

  const [loadingField, setLoadingField] = useState(null);
  const [successField, setSuccessField] = useState(null);

  const fieldsConfig = [
    { label: 'وجه البطاقة الموحدة لمقدم الطلب', field: 'front_id' },
    { label: 'ضهر البطاقة الموحدة', field: 'back_id' },
    { label: 'بطاقة السكن', field: 'residence_card' },
  ];

  useEffect(() => {
    // Register validation rules for file uploads
    register('front_id', { required: 'يرجى إرفاق وجه البطاقة الموحدة' });
    register('back_id', { required: 'يرجى إرفاق ضهر البطاقة الموحدة' });
    register('residence_card', { required: 'يرجى إرفاق بطاقة السكن' });
  }, [register]);

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;
  
    setLoadingField(field);
    setSuccessField(null);
  
    try {
      let compressedFile = file;
  
      // 🖼️ Compress only if it's an image
      if (file.type.startsWith("image/")) {
        compressedFile = await imageCompression(file, {
          maxSizeMB: 1, // ~1MB
          maxWidthOrHeight: 1280,
          useWebWorker: true
        });
      }
  
      // ✅ Set the file to react-hook-form
      await setValue(field, compressedFile);
  
      // ✅ Immediately trigger validation to clear errors if valid
      await trigger(field);
  
      // ✅ Mark upload as successful
      setSuccessField(field);
    } catch (error) {
      console.error("❌ Compression or upload failed:", error);
    } finally {
      setLoadingField(null);
    }
  };

  const handleNext = async () => {
    const valid = await trigger(['front_id', 'back_id', 'residence_card']);
    if (valid) {
      onNext();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>إرفاق المستندات المطلوبة</Typography>

      <Grid container spacing={3}>
        {fieldsConfig.map(({ label, field }) => (
          <Grid item xs={12} key={field}>
            <Button variant="outlined" component="label" fullWidth sx={{ justifyContent: 'space-between' }}>
              {label}
              <input type="file" hidden onChange={(e) => handleFileUpload(e, field)} />
              {loadingField === field ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                getValues(field)?.name && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'green' }}>
                    {getValues(field)?.name}
                  </Typography>
                )
              )}
            </Button>

            {/* Show validation error if present */}
            {errors?.[field] && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errors[field]?.message}
              </Typography>
            )}
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="contained" color="secondary" onClick={onPrevious}>
          السابق
        </Button>
        <Button variant="contained" color="primary" onClick={handleNext}>
          التالي
        </Button>
      </Box>

      {/* Snackbar for upload success */}
      <Snackbar
        open={!!successField}
        autoHideDuration={4000}
        onClose={() => setSuccessField(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessField(null)} sx={{ width: '100%' }}>
          تم تحميل {successField === 'front_id' ? 'وجه البطاقة' :
                    successField === 'back_id' ? 'ضهر البطاقة' :
                    successField === 'residence_card' ? 'بطاقة السكن' : ''} بنجاح
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Attachments;
